<template>
  <q-layout view="hHh lpr lff" class="bg-grey-1">
    <!-- Sidebar/Drawer -->
    <q-drawer v-model="sidebarOpen" show-if-above :width="260" :breakpoint="1024" bordered class="bg-grey-2">
      <q-list padding v-if="!loading">
        <router-link to="/my-events" class="justify-center back-button">
          <q-btn color="primary" :label="$t('dashboard.menu.backBtn')" icon="arrow_back" />
        </router-link>
        <SideBar />
      </q-list>
      <q-list padding v-else>
        <q-skeleton class="q-mx-sm q-my-sm" type="rect" height="50px" rounded />
        <q-skeleton class="q-mx-sm q-my-sm" type="rect" height="50px" rounded />
        <q-skeleton class="q-mx-sm q-my-sm" type="rect" height="50px" rounded />
        <q-skeleton class="q-mx-sm q-my-sm" type="rect" height="50px" rounded />
      </q-list>
    </q-drawer>
    <!-- Main dashboard layout -->
    <q-page-container>
      <q-page padding class="dashboard">
        <!-- Header -->
        <div class="dashboard-header">
          <q-btn v-if="!sidebarOpen" class="dashboard-header__drawer-button" flat @click="openSidebar" round dense>
            <q-icon name="east" />
            <q-icon name="space_dashboard" />
          </q-btn>
          <q-select v-model="selectedCurrencyOption" :options="currencyOptions" :label="$t('dashboard.menu.currencyBtn')" @update:model-value="handleCurrencyChange"
            class="currency-selector" />
        </div>
        <!-- Summary cards/Charts -->
        <div v-if="!loading && !currentEvent" class="text-subtitle1">
          {{ $t("events.loadingDashboardMessage") }}
        </div>
        <div v-else class="dashboard-wrapper">
          <div class="dashboard-summary">
            <div class="dashboard-summary__info">
              <SummaryStats v-if="currentEvent && currentEvent.name" :event="currentEvent" />
            </div>
            <q-card class="dashboard-summary__sales">
              <SummaryCardSales v-if="currentEvent && currentEvent.name" :event="currentEvent" />
            </q-card>
            <q-card class="dashboard-summary__chart">
              <ChartTabs :event-id="Number($route.params.eventId)" />
            </q-card>
          </div>
          <!-- Orders/Shares tables -->
          <div class="dashboard-summary__table">
            <AlbumViewsTable v-if="currentEvent && canViewAlbumViewsTable" :event="currentEvent" />
            <OrdersTable v-if="currentEvent.id && canViewCompletedOrders" :event-id="currentEvent.id" />
            <SponsorAssetsTable v-if="currentEvent.id && isSponsor" :event-id="currentEvent.id" />
            <SharesTable v-if="currentEvent && currentEvent.name" :event="currentEvent" />
          </div>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import ChartTabs from "@/pages/dashboardEvents/dashboardEventsSingle/components/charts/ChartTabs.vue";
import SummaryCardSales from "@/pages/dashboardEvents/dashboardEventsSingle/components/SummaryCardSales.vue";
import SummaryStats from "@/pages/dashboardEvents/dashboardEventsSingle/components/SummaryStats.vue";
import OrdersTable from "@/pages/dashboardEvents/dashboardEventsSingle/components/OrdersTable.vue";
import AlbumViewsTable from "@/pages/dashboardEvents/dashboardEventsSingle/components/AlbumViewsTable.vue";
import SharesTable from "@/pages/dashboardEvents/dashboardEventsSingle/components/SharesTable.vue";
import SideBar from "@/pages/dashboardEvents/dashboardEventsSingle/components/SideBar.vue";
import SponsorAssetsTable from "@/pages/dashboardEvents/dashboardEventsSingle/components/SponsorAssetsTable.vue";
import userRoleMixin from '@/mixins/userRoleMixin.js';

export default {
  name: "DashboardEventsSinglePage",
  components: {
    SummaryStats,
    SummaryCardSales,
    ChartTabs,
    OrdersTable,
    SharesTable,
    SponsorAssetsTable,
    SideBar,
    AlbumViewsTable
  },
  mixins: [userRoleMixin],
  data() {
    return {
      selectedCurrencyOption: null,
    };
  },
  computed: {
    ...mapState("dashboardEvents", [
      "links",
      "events",
      "eventId",
      "loading",
      "isSidebarOpen",
    ]),
    ...mapGetters("dashboardEvents", [
      "getCurrentEvent",
      "getCurrencies",
      "getCurrency",
    ]),
    isSponsor() {
      return this.userRole === 'sponsor';
    },
    currencyOptions() {
      return this.getCurrencies.map((currency) => ({
        label: currency,
        value: currency,
      }));
    },
    sidebarOpen: {
      get() {
        return this.isSidebarOpen;
      },
      set(value) {
        this.toggleSidebar(value);
      },
    },
    currentEvent() {
      return this.getCurrentEvent || {};
    },
    charts() {
      if (!this.currentEvent || !this.currentEvent.timeSeriesData) {
        return [];
      }
      return [
        {
          id: 1,
          data: this.currentEvent.timeSeriesData.albumViewsOverTime || [],
          label: this.$t("dashboard.main.albumViews"),
          title: this.$t("dashboard.main.albumViews"),
          backgroundColor: "rgba(54, 162, 235, 0.2)",
          borderColor: "rgba(54, 162, 235, 1)",
        },
        {
          id: 2,
          data: this.currentEvent.timeSeriesData.ordersOverTime || [],
          label: this.$t("dashboard.main.totalOrders"),
          title: this.$t("dashboard.main.totalOrders"),
          backgroundColor: "rgba(255, 99, 132, 1)",
          borderColor: "rgba(255, 99, 132, 1)",
        },
        {
          id: 3,
          data: this.currentEvent.timeSeriesData.salesAmountOverTime || [],
          label: this.$t("dashboard.main.totalAlbumSalesAmount"),
          title: this.$t("dashboard.main.totalAlbumSalesAmount"),
          backgroundColor: "rgba(255, 206, 86, 0.2)",
          borderColor: "rgba(255, 206, 86, 1)",
        },
      ];
    },
  },
  methods: {
    ...mapActions("dashboardEvents", [
      "fetchEventById",
      "toggleSidebar",
      "setCurrency",
      "fetchCurrencies",
    ]),
    openSidebar() {
      this.toggleSidebar(true);
    },
    handleCurrencyChange(currency) {
      this.setCurrency(currency.value);
      this.fetchEventById({
        eventId: this.$route.params.eventId,
        currency: currency.value,
      });
    },
  },
  watch: {
    "$route.params.eventId": {
      immediate: true,
      async handler(newEventId, oldEventId) {
        if (!newEventId) {
          return
        }
        if (newEventId && oldEventId && newEventId !== oldEventId) {
          return;
        }

        await this.fetchEventById({
          eventId: newEventId,
          currency: this.selectedCurrencyOption,
        });
      },
    },
  },
  async created() {
    if (!this.getCurrencies.length) {
      await this.fetchCurrencies()
    }

    if (!this.currentEvent) {
      this.fetchEventById();
    }
    this.selectedCurrencyOption = this.getCurrency || this.getCurrencies[0]
  }
};
</script>

<style lang="scss">
.dashboard {
  margin: 0 auto;
  max-width: 2000px;

  &-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &-header {
    padding-bottom: 1rem;
    display: flex;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;

    &__drawer-button {
      width: 80px;
    }
  }
}

.q-card {
  box-shadow: 1px 1px 1px rgba(0, 0, 0, 0.05), 0 2px 2px rgba(0, 0, 0, 0.05)
}

.dashboard-summary {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-content: flex-start;
  gap: 10px;
  width: 100%;
  margin-bottom: 12px;

  &__info {
    margin: 0 auto;
    min-width: 250px;
    height: 100%;
    border-radius: 6px;
    flex-basis: 100%;
  }

  &__sales {
    flex-basis: 100%;
    border-radius: 6px;
  }

  &__chart {
    overflow: hidden;
    width: 100%;
    flex-grow: 1;
    border-radius: 6px;

    & div {
      text-transform: capitalize;
      font-weight: 600;
      width: auto;

      .q-tab__indicator {
        opacity: 1;
        background: #999;
      }

      .q-tab--active .q-tab__indicator {
        opacity: 1;
        background: $primary;
      }
    }
  }

  &__table {
    width: 100%;
    flex-basis: 100%;
  }
}

.back-button {
  display: flex;
  width: 100%;
  justify-content: center;
  padding: 0 4px;
  margin-bottom: 10px;
  text-decoration: none;

  &>button {
    width: 90%;
    border-radius: 60px;
    align-items: center;
  }
}

.currency-selector {
  margin-left: auto;
  padding: 10px;
  width: 190px;
}

.dashboard__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
}


@media (min-width: $breakpoint-sm-min) {
  .dashboard-summary {
    &__chart {
      flex-basis: 100%;
    }

    &__table {
      width: 100%;
    }
  }
}

@media (min-width: $breakpoint-md-min) {
  .dashboard-summary {
    &__sales {
      flex-basis: 33%;
    }

    &__chart {
      flex-basis: 65%;
    }
  }
}
</style>
